/**
 * StatsBarChart - Animated bar chart showing recent game stats vs prop line
 * Features: animated bars, stats summary row, animated tabs, tap-to-expand game detail,
 * gradient fills, live slider updates, responsive layout
 */

import { plotFraction } from '@parlay-picker/shared/chartScale';
import { leagueRankTier, leagueTeamCount, type RankTier } from '@parlay-picker/shared/leagueScales';
import { betOutcome, hitSummary } from '@parlay-picker/shared/lineOutcome';
import {
  computeGameComparison,
  computeNiceTicks,
  computeValueIndicator,
  filterGamesByLocation,
  formatDate,
  formatDateCompact,
  formatDateLong,
  gameChartLabel,
  getStatValueForPeriod,
  type LocationFilter,
  marketKeyToChartPeriod,
} from '@parlay-picker/shared/statsChart';
import { cn } from '@utils/cn';
import { AnimatePresence, motion } from 'framer-motion';
import { GitCompareArrows, Info } from 'lucide-react';
import { type ReactElement, useCallback, useMemo, useState } from 'react';
import { AnimatedBar } from './stats-chart/AnimatedBar';
import { GameDetailCard } from './stats-chart/GameDetailCard';
import { StatsSummaryRow } from './stats-chart/StatsSummaryRow';
import type { ChartDataPoint, StatsBarChartProps, ViewMode } from './stats-chart/types';
import { ViewModeTabs } from './stats-chart/ViewModeTabs';

// GameData re-exported from shared for existing consumers
export type { GameData } from '@parlay-picker/shared/statsChart';

// A top-third defense is hard to score on, so it reads as danger for the bettor
const DEF_RANK_BADGE_CLASS: Record<RankTier, string> = {
  top: 'bg-theme-danger/15 text-theme-danger',
  middle: 'bg-theme-warning/15 text-theme-warning',
  bottom: 'bg-theme-success/15 text-theme-success',
};

function defRankBadgeClass(tier: RankTier | null): string {
  return tier ? DEF_RANK_BADGE_CLASS[tier] : 'bg-theme-surface/60 text-theme-text-secondary';
}

export default function StatsBarChart({
  recentGames,
  h2hGames,
  propLine,
  isAlternateLine,
  statKey,
  opponentAbbr,
  overUnder,
  odds,
  opponentDefRank,
  leagueKey,
  isGameProp,
  marketKey,
}: StatsBarChartProps): ReactElement | null {
  const [viewMode, setViewMode] = useState<ViewMode>('L5');
  const [expandedBar, setExpandedBar] = useState<number | null>(null);
  const [locationFilter, setLocationFilter] = useState<LocationFilter>('all');
  const period = useMemo(() => marketKeyToChartPeriod(marketKey), [marketKey]);
  const [compareMode, setCompareMode] = useState(false);
  const [compareIndices, setCompareIndices] = useState<number[]>([]);

  const isUnderBet = overUnder?.toLowerCase() === 'under';

  // Determine which games to show based on view mode, then apply location filter
  const displayGames = useMemo(() => {
    if (!recentGames?.length) return [];
    let games: typeof recentGames;
    if (viewMode === 'H2H') {
      games = h2hGames?.slice(0, 5) || [];
    } else {
      const count = viewMode === 'L5' ? 5 : viewMode === 'L10' ? 10 : 20;
      games = recentGames.slice(0, count);
    }
    // Apply home/away filter (player props only)
    if (!isGameProp && locationFilter !== 'all') {
      games = filterGamesByLocation(games, locationFilter);
    }
    return games;
  }, [viewMode, recentGames, h2hGames, locationFilter, isGameProp]);

  // Check if any games have isHome data (new field — stale cache won't have it)
  const hasHomeAwayData = useMemo(
    () => (recentGames || []).some((g) => g.isHome !== undefined),
    [recentGames],
  );

  // Build chart data (reversed for chronological order)
  const chartData: ChartDataPoint[] = useMemo(
    () =>
      [...displayGames].reverse().map((g, idx) => {
        const { value, hasPeriodData } = getStatValueForPeriod(g, statKey, period);
        return {
          value,
          date: g.date,
          gameNumber: idx + 1,
          opponent: g.opponent,
          min: g.min,
          dnp: g.dnp || false,
          label: gameChartLabel(formatDate(g.date) || `G${idx + 1}`, g.isPreseason),
          compactLabel: gameChartLabel(formatDateCompact(g.date) || `${idx + 1}`, g.isPreseason),
          dateFormatted: formatDateLong(g.date),
          // Team game context (if available)
          isHome: g.isHome,
          teamScore: g.teamScore,
          opponentScore: g.opponentScore,
          won: g.won,
          hasPeriodData,
        };
      }),
    [displayGames, statKey, period],
  );

  const handleBarClick = useCallback(
    (index: number) => {
      if (compareMode) {
        setCompareIndices((prev) => {
          if (prev.length === 0) return [index];
          if (prev.length === 1) return prev[0] === index ? [] : [prev[0], index];
          // Already have 2 — start fresh with new selection
          return [index];
        });
        return;
      }
      setExpandedBar(expandedBar === index ? null : index);
    },
    [expandedBar, compareMode],
  );

  const handleModeChange = useCallback((mode: ViewMode) => {
    setViewMode(mode);
    setExpandedBar(null);
    setCompareMode(false);
    setCompareIndices([]);
  }, []);

  // Count how many displayed games have period data (for info banner)
  const periodDataCount = useMemo(() => {
    if (period === 'full') return displayGames.length;
    return displayGames.filter(
      (g) => !g.dnp && getStatValueForPeriod(g, statKey, period).hasPeriodData,
    ).length;
  }, [displayGames, statKey, period]);

  // Calculate stats (must be before early return to satisfy rules-of-hooks). A push is neither a
  // hit nor a miss, so rates are over decided games (Bryan, 2026-11-02).
  const { playedGames, hits, decided, pushes, hitRate, dnpCount, directionLabel, dataMax } =
    useMemo(() => {
      const played = chartData.filter((g) => !g.dnp);
      const side = isUnderBet ? 'under' : 'over';
      const summary = hitSummary(
        played.map((g) => betOutcome(g.value, propLine, side, isAlternateLine)),
      );
      const values = played.map((g) => g.value);
      return {
        playedGames: played,
        ...summary,
        hitRate: summary.decided > 0 ? Math.round((summary.hits / summary.decided) * 100) : 0,
        dnpCount: chartData.filter((g) => g.dnp).length,
        directionLabel: side,
        dataMax: values.length > 0 ? Math.max(...values, propLine) : propLine,
      };
    }, [chartData, isUnderBet, propLine, isAlternateLine]);

  // Value indicator (when odds are available), against the decided-game hit rate
  const valueIndicator = useMemo(() => {
    if (!odds || decided === 0) return null;
    return computeValueIndicator(hits / decided, odds);
  }, [odds, hits, decided]);

  // Compare mode data
  const comparison = useMemo(() => {
    if (!compareMode || compareIndices.length !== 2) return null;
    const g1 = displayGames[displayGames.length - 1 - compareIndices[0]];
    const g2 = displayGames[displayGames.length - 1 - compareIndices[1]];
    if (!g1 || !g2) return null;
    return computeGameComparison(g1, g2, statKey);
  }, [compareMode, compareIndices, displayGames, statKey]);

  const { ticks: yTicks, minValue, maxValue } = computeNiceTicks(0, dataMax);

  if (!recentGames?.length || !propLine) return null;

  const linePercent = plotFraction(propLine, minValue, maxValue) * 100;

  const hasH2hData = (h2hGames?.length ?? 0) > 0;

  return (
    <div className="bg-theme-bg/50 rounded-xl p-3 space-y-2">
      {/* Header: animated tabs + controls + hit rate */}
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-1.5">
          <ViewModeTabs
            activeMode={viewMode}
            onModeChange={handleModeChange}
            recentGamesCount={recentGames.length}
            hasH2hData={hasH2hData}
            opponentAbbr={opponentAbbr}
          />
          {/* Compare toggle */}
          <button
            type="button"
            onClick={() => {
              setCompareMode((p) => !p);
              setCompareIndices([]);
              if (compareMode) setExpandedBar(null);
            }}
            disabled={playedGames.length < 2}
            className={cn(
              'p-1 rounded-md transition-colors',
              compareMode
                ? 'bg-theme-primary-fill text-theme-on-primary-fill'
                : 'bg-theme-surface/80 text-theme-text-tertiary hover:text-theme-text-secondary',
              'disabled:opacity-40 disabled:cursor-not-allowed',
            )}
            title={playedGames.length < 2 ? 'Need 2+ games to compare' : 'Compare games'}
          >
            <GitCompareArrows className="w-3.5 h-3.5" />
          </button>
        </div>
        <motion.span
          key={`${hits}-${decided}-${pushes}`}
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          className={cn(
            'text-xs font-bold tabular-nums shrink-0 text-right',
            decided === 0
              ? 'text-theme-text-tertiary'
              : hitRate >= 60
                ? 'text-theme-success'
                : hitRate >= 40
                  ? 'text-theme-warning'
                  : 'text-theme-danger',
          )}
        >
          {playedGames.length === 0 ? (
            'No data'
          ) : (
            <>
              {decided > 0
                ? `${hits}/${decided} ${directionLabel} (${hitRate}%)`
                : 'No decided games'}
              {(pushes > 0 || dnpCount > 0) && (
                // Below sm the counts take their own line, so the header fits a 375px screen
                <span className="block sm:inline">
                  <span className="hidden sm:inline"> · </span>
                  {[pushes > 0 ? `${pushes} push` : null, dnpCount > 0 ? `${dnpCount} DNP` : null]
                    .filter(Boolean)
                    .join(' · ')}
                </span>
              )}
            </>
          )}
        </motion.span>
      </div>

      {/* Home/Away filter + DEF rank badge (player props only, hide filter when no isHome data) */}
      {!isGameProp && (hasHomeAwayData || opponentDefRank) && (
        <div className="flex items-center gap-1">
          {hasHomeAwayData &&
            (['all', 'home', 'away'] as const).map((filter) => (
              <button
                key={filter}
                type="button"
                onClick={() => setLocationFilter(filter)}
                className={cn(
                  'px-2 py-1 text-[10px] font-semibold rounded-md transition-colors capitalize',
                  locationFilter === filter
                    ? 'bg-theme-primary-fill text-theme-on-primary-fill'
                    : 'bg-theme-surface/60 text-theme-text-tertiary hover:text-theme-text-secondary',
                )}
              >
                {filter}
              </button>
            ))}
          {/* DEF rank badge */}
          {opponentDefRank && (
            <div
              className={cn(
                'ml-auto px-2 py-1 text-[10px] font-bold rounded-md',
                defRankBadgeClass(leagueRankTier(opponentDefRank, leagueKey)),
              )}
              title={`Opponent overall defensive rating rank: #${opponentDefRank} (1=best defense, ${leagueTeamCount(leagueKey) ?? 'last'}=worst)`}
            >
              DEF #{opponentDefRank}
            </div>
          )}
        </div>
      )}

      {/* Stats summary row */}
      <StatsSummaryRow
        chartData={chartData}
        propLine={propLine}
        isUnderBet={isUnderBet}
        isAlternateLine={isAlternateLine}
        statKey={statKey}
      />

      {/* Period data info banner */}
      {period !== 'full' &&
        displayGames.length > 0 &&
        periodDataCount < displayGames.filter((g) => !g.dnp).length && (
          <div className="flex items-center gap-1.5 px-2 py-1 rounded-lg bg-theme-warning/10 text-theme-warning text-[11px]">
            <Info className="w-3 h-3 shrink-0" />
            <span>
              {periodDataCount === 0
                ? `${period.toUpperCase()} data unavailable — showing full-game stats`
                : `${period.toUpperCase()} data available for ${periodDataCount}/${displayGames.filter((g) => !g.dnp).length} games`}
            </span>
          </div>
        )}

      {/* Value indicator row (when odds available) */}
      {valueIndicator && (
        <div className="flex items-center gap-2 bg-theme-surface/60 rounded-lg px-3 py-2">
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between text-xs mb-1">
              <span className="text-theme-text-secondary">
                Hit {hitRate}%
                <span className="text-theme-text-tertiary ml-1">
                  ({hits}/{decided}
                  {pushes > 0 ? `, ${pushes} push` : ''})
                </span>
              </span>
              <span className="text-theme-text-tertiary">
                Implied {Math.round(valueIndicator.impliedProb * 100)}% ({odds})
              </span>
            </div>
            <div className="relative h-1.5 bg-theme-surface-active rounded-full overflow-hidden">
              <motion.div
                initial={{ width: 0 }}
                animate={{ width: `${Math.min(Math.abs(valueIndicator.edge) * 100, 100)}%` }}
                className={cn(
                  'absolute top-0 left-0 h-full rounded-full',
                  valueIndicator.hasValue ? 'bg-theme-success' : 'bg-theme-danger',
                )}
              />
            </div>
          </div>
          <span
            className={cn(
              'text-xs font-bold shrink-0 tabular-nums',
              valueIndicator.hasValue ? 'text-theme-success' : 'text-theme-danger',
            )}
          >
            {valueIndicator.label}
          </span>
        </div>
      )}

      {/* Compare mode banner */}
      {compareMode && (
        <div className="flex items-center justify-between bg-theme-primary/15 rounded-lg px-3 py-1.5">
          <span className="text-[10px] font-semibold text-theme-primary">
            Compare Mode —{' '}
            {compareIndices.length === 0
              ? 'tap first game'
              : compareIndices.length === 1
                ? 'tap second game'
                : 'comparison ready'}
          </span>
          <button
            type="button"
            onClick={() => {
              setCompareMode(false);
              setCompareIndices([]);
            }}
            className="text-[10px] text-theme-text-tertiary hover:text-theme-text transition"
          >
            Exit
          </button>
        </div>
      )}

      {/* Chart area */}
      <AnimatePresence mode="wait">
        {chartData.length > 0 ? (
          <motion.div
            key={`${viewMode}-${statKey}-${locationFilter}`}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.15 }}
          >
            {/* Bar chart with Y-axis labels */}
            <div className="relative flex">
              {/* Y-axis labels, each centred on its gridline: same insets as the grid lines below */}
              <div className="relative shrink-0 w-8">
                <div className="absolute inset-x-0 top-4 bottom-5">
                  {yTicks.map((tick) => (
                    <span
                      key={tick}
                      className="absolute right-1.5 translate-y-1/2 text-[9px] text-theme-text-tertiary/60 tabular-nums text-right leading-none"
                      style={{ bottom: `${plotFraction(tick, minValue, maxValue) * 100}%` }}
                    >
                      {tick}
                    </span>
                  ))}
                </div>
              </div>

              {/* Chart area */}
              <div className="relative h-48 sm:h-56 flex items-stretch gap-0.5 flex-1 min-w-0 pt-4 pb-5">
                {/* Grid lines */}
                <div className="absolute inset-x-0 top-4 bottom-5 pointer-events-none flex flex-col justify-between">
                  {yTicks.map((tick) => (
                    <div key={tick} className="border-t border-theme-border/40" />
                  ))}
                </div>

                {/* Prop line */}
                <div className="absolute inset-x-0 top-4 bottom-5 z-10 pointer-events-none">
                  <motion.div
                    className="absolute left-0 right-0 translate-y-1/2"
                    animate={{ bottom: `${linePercent}%` }}
                    transition={{ type: 'spring', stiffness: 300, damping: 30 }}
                  >
                    <div className="flex items-center gap-1">
                      <div className="flex-1 border-t-2 border-dashed border-theme-primary/70" />
                      <motion.span
                        key={propLine}
                        initial={{ opacity: 0.5, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        className="text-[10px] font-bold text-theme-primary bg-theme-bg/80 px-1.5 py-0.5 rounded shrink-0"
                      >
                        {propLine}
                      </motion.span>
                    </div>
                  </motion.div>
                </div>

                {/* Bars */}
                {chartData.map((point, idx) => (
                  <AnimatedBar
                    key={`${viewMode}-${statKey}-${locationFilter}-${point.date || idx}`}
                    point={point}
                    maxValue={maxValue}
                    minValue={minValue}
                    propLine={propLine}
                    isUnderBet={isUnderBet}
                    isAlternateLine={isAlternateLine}
                    isExpanded={compareMode ? compareIndices.includes(idx) : expandedBar === idx}
                    onClick={() => handleBarClick(idx)}
                    index={idx}
                    barCount={chartData.length}
                    statKey={statKey}
                  />
                ))}
              </div>
            </div>

            {/* Compare mode: comparison card */}
            <AnimatePresence>
              {comparison && compareIndices.length === 2 && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  className="overflow-hidden"
                >
                  <div className="flex items-center justify-between rounded-lg px-3 py-2 mt-2 border border-theme-primary/20 bg-theme-primary/5">
                    <div className="flex items-center gap-4 text-xs">
                      <div className="text-center">
                        <div className="text-theme-text-tertiary text-[10px]">
                          {formatDate(comparison.game1Date)}
                        </div>
                        <div className="font-bold text-theme-text">{comparison.game1Value}</div>
                        {comparison.game1Opponent && (
                          <div className="text-[10px] text-theme-text-tertiary">
                            vs {comparison.game1Opponent}
                          </div>
                        )}
                      </div>
                      <div className="text-center">
                        <div className="text-[10px] text-theme-text-tertiary">vs</div>
                      </div>
                      <div className="text-center">
                        <div className="text-theme-text-tertiary text-[10px]">
                          {formatDate(comparison.game2Date)}
                        </div>
                        <div className="font-bold text-theme-text">{comparison.game2Value}</div>
                        {comparison.game2Opponent && (
                          <div className="text-[10px] text-theme-text-tertiary">
                            vs {comparison.game2Opponent}
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="text-right">
                      <div
                        className={cn(
                          'text-sm font-bold',
                          comparison.delta > 0
                            ? 'text-theme-success'
                            : comparison.delta < 0
                              ? 'text-theme-danger'
                              : 'text-theme-text-tertiary',
                        )}
                      >
                        {comparison.delta > 0 ? '+' : ''}
                        {comparison.delta}
                      </div>
                      <div className="text-[10px] text-theme-text-tertiary">
                        {comparison.percentChange > 0 ? '+' : ''}
                        {comparison.percentChange.toFixed(0)}%
                      </div>
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Expanded game detail (normal mode only) */}
            <AnimatePresence>
              {!compareMode && expandedBar !== null && chartData[expandedBar] && (
                <GameDetailCard
                  point={chartData[expandedBar]}
                  statKey={statKey}
                  propLine={propLine}
                  isUnderBet={isUnderBet}
                  isAlternateLine={isAlternateLine}
                  onClose={() => setExpandedBar(null)}
                />
              )}
            </AnimatePresence>
          </motion.div>
        ) : (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="h-48 flex items-center justify-center text-xs text-theme-text-tertiary"
          >
            {locationFilter !== 'all'
              ? `No ${locationFilter} games in this window`
              : viewMode === 'H2H'
                ? `No games vs ${opponentAbbr} in recent history`
                : 'No game data available'}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Legend */}
      <div className="flex items-center justify-center gap-3 text-[10px] text-theme-text-tertiary pt-1">
        <div className="flex items-center gap-1">
          <div className="w-2.5 h-2.5 rounded-sm bg-theme-success" />
          <span>Hit</span>
        </div>
        <div className="flex items-center gap-1">
          <div className="w-2.5 h-2.5 rounded-sm bg-theme-danger" />
          <span>Miss</span>
        </div>
        {pushes > 0 && (
          <div className="flex items-center gap-1">
            <div className="w-2.5 h-2.5 rounded-sm bg-theme-text-tertiary" />
            <span>Push</span>
          </div>
        )}
        <div className="flex items-center gap-1">
          <div className="w-2.5 h-2.5 rounded-sm bg-theme-surface-active" />
          <span>DNP</span>
        </div>
        <div className="flex items-center gap-1">
          <div className="w-4 h-0 border-t-2 border-dashed border-theme-primary" />
          <span>Line</span>
        </div>
      </div>
    </div>
  );
}
